import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReplayIcon from '@mui/icons-material/Replay';
import { CreateLimits } from 'components/dialog/limits/create';
import { UpdateLimits } from 'components/dialog/limits/update';
import { DeleteLimits } from 'components/dialog/limits/delete';
import { InfoLimits } from 'components/dialog/limits/read';
import {
    buildSectionSubpageActionPath,
    buildSectionSubpageBasePath,
    parseSectionSubpageActionRoute,
} from 'utils/sectionSubpageRouting';

interface SubMenuProps {
    selected: any;
    cleanSelected: () => void;
    rows: any;
    contextMenu: any;
    setContextMenu: (value: any) => void;
    reloading: () => void;
}

const SubMenu: FC<SubMenuProps> = ({ selected, cleanSelected, rows, contextMenu, setContextMenu, reloading }) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const [openAdd, setOpenAdd] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openShow, setOpenShow] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);

    const modalRoute = parseSectionSubpageActionRoute(location.pathname, 'accruals', 'limits', ['add', 'update', 'detail']);
    const routeSelected = useMemo(() => {
        if (!modalRoute.id) return null;
        return rows.find((row: any) => String(row?.id) === String(modalRoute.id)) || null;
    }, [modalRoute.id, rows]);
    const selectedForDialog = routeSelected ? [routeSelected] : selected;

    const closeToBase = () => {
        const basePath = buildSectionSubpageBasePath('accruals', 'limits');
        if (modalRoute.action) {
            navigate(basePath);
            return;
        }

        setOpenAdd(false);
        setOpenEdit(false);
        setOpenShow(false);
    };

    const options = [
        {
            name: t('accruals.limits.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSectionSubpageActionPath('accruals', 'limits', 'add'));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: false,
            onlyOne: false,
        },
        {
            name: t('accruals.limits.context_menu.info'),
            icon: <VisibilityIcon />,
            click: () => {
                if (!selected?.[0]?.id) return;
                navigate(buildSectionSubpageActionPath('accruals', 'limits', 'detail', selected[0].id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: selected.length <= 0,
            onlyOne: false,
        },
        {
            name: t('accruals.limits.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                if (!selected?.[0]?.id) return;
                navigate(buildSectionSubpageActionPath('accruals', 'limits', 'update', selected[0].id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: selected.length !== 1,
            onlyOne: true,
        },
        {
            name: t('accruals.limits.context_menu.delete'),
            icon: <DeleteIcon color='error' />,
            click: () => {
                setOpenDelete(true);
                setContextMenu(null);
            },
            color: 'error',
            divider: false,
            disabled: true,
            onlyOne: false,
        },
        {
            name: t('accruals.limits.context_menu.reload'),
            icon: <ReplayIcon />,
            click: () => {
                reloading();
                setContextMenu(null);
            },
            color: '',
            divider: true,
            disabled: false,
            onlyOne: false,
        },
    ];

    return (
        <>
            <Menu
                open={Boolean(contextMenu)}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
            >
                {options.map((option) => (
                    <div key={option.name} hidden={option.onlyOne && selected?.length !== 1}>
                        {option.divider && <Divider />}
                        <MenuItem onClick={option.click} disabled={option.disabled}>
                            <ListItemIcon>{option.icon}</ListItemIcon>
                            <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />
                        </MenuItem>
                    </div>
                ))}
            </Menu>

            <CreateLimits
                open={modalRoute.action === 'add' || openAdd}
                setOpen={(value: boolean) => {
                    if (!value) {
                        closeToBase();
                    } else {
                        setOpenAdd(true);
                    }
                }}
                rows={rows}
                reloading={reloading}
            />
            <InfoLimits
                open={modalRoute.action === 'detail' || openShow}
                setOpen={(value: boolean) => {
                    if (!value) {
                        closeToBase();
                    } else {
                        setOpenShow(true);
                    }
                }}
                rowToEdit={selectedForDialog[0]}
                cleanSelected={cleanSelected}
            />
            <UpdateLimits
                open={modalRoute.action === 'update' || openEdit}
                setOpen={(value: boolean) => {
                    if (!value) {
                        closeToBase();
                    } else {
                        setOpenEdit(true);
                    }
                }}
                rows={rows}
                rowToEdit={selectedForDialog[0]}
                reloading={reloading}
                cleanSelected={cleanSelected}
            />
            <DeleteLimits open={openDelete} setOpen={setOpenDelete} rowToEdit={selected[0]} reloading={reloading} />
        </>
    );
};

export { SubMenu };
